import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import * as moment from "moment";
import { Link } from "react-router-dom";
import classnames from "classnames";
import Flatpickr from "react-flatpickr";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";

import { useSelector, useDispatch } from "react-redux";

import { getProjects as onGetProjects } from "../../../slices/thunks";

import {
  getPhases as onGetPhases,
  addNewPhase as onAddNewPhase,
  updatePhase as onUpdatePhase,
  deletePhase as onDeletePhase,
} from "../../../slices/thunks";

import Loader from "../../../Components/Common/Loader";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { createSelector } from "reselect";
import { useProfile } from "../../../Components/Hooks/UserHooks";

const PhaseMenegement = () => {
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [projects, setProjects] = useState([]);

  const dispatch = useDispatch();

  const selectLayoutState = (state) => state.Phases;
  const selectLayoutProperties = createSelector(selectLayoutState, (phase) => ({
    phases: phase.phases,
    isPhaseSuccess: phase.isPhaseSuccess,
    error: phase.error,
  }));
  const { phases, isPhaseSuccess, error } = useSelector(selectLayoutProperties);
  const { userProfile } = useProfile();

  useEffect(() => {
    dispatch(onGetProjects()).then((response) => {
      setProjects(response.payload || []);
    });
  }, [dispatch]);

  const isAdmin = userProfile?.user?.groups?.includes("Admin");

  const [phaseList, setPhaseList] = useState([]);
  const [phase, setPhase] = useState([]);

  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const onClickDelete = (phase) => {
    setPhase(phase);
    setDeleteModal(true);
  };

  const handleDeletePhase = () => {
    if (phase) {
      dispatch(onDeletePhase(phase.id));
      setDeleteModal(false);
    }
  };

  useEffect(() => {
    setPhaseList(phases);
  }, [phases]);

  useEffect(() => {
    if (!isEmpty(phases)) setPhaseList(phases);
  }, [phases]);

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      let filteredPhases = phases;
      if (type !== "all") {
        filteredPhases = phases.filter((phase) => phase.status === type);
      }
      setPhaseList(filteredPhases);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: (phase && phase.id) || "",
      project: phase?.project || null,
      name: phase?.name || "",
      description: phase?.description || "",
      starting_date: phase?.starting_date || null,
      estimated_finish_date: phase?.estimated_finish_date || null,
      actual_finish_date: phase?.actual_finish_date || null,
      equity_capital: phase?.equity_capital ?? "",
      active: phase?.active || true,
    },
    validationSchema: Yup.object({
      project: Yup.string().required("Project is required"),
      name: Yup.string()
        .required("Please Enter Name")
        .max(254, "Name must be at under 254 characters")
        .min(3, "Name must be at least 3 characters"),
      description: Yup.string().required("Please Enter Description"),
      active: Yup.boolean().required("Please specify if the phase is active"),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updatedPhase = {
          id: phase?.id || 0,
          project: values.project,
          name: values.name,
          description: values.description,
          starting_date: values.starting_date,
          estimated_finish_date: values.estimated_finish_date,
          actual_finish_date: values.actual_finish_date,
          equity_capital: values.equity_capital === "" ? null : values.equity_capital,
          active: values.active,
        };
        dispatch(onUpdatePhase(updatedPhase));
        validation.resetForm();
      } else {
        const newPhase = {
          project: values.project,
          name: values.name,
          description: values.description,
          starting_date: values.starting_date,
          estimated_finish_date: values.estimated_finish_date,
          actual_finish_date: null,
          equity_capital: values.equity_capital === "" ? null : values.equity_capital,
          active: values.active,
        };
        dispatch(onAddNewPhase(newPhase));
        validation.resetForm();
      }
      toggle();
    },
  });

  useEffect(() => {
    dispatch(onGetPhases());
  }, [dispatch]);

  useEffect(() => {
    setPhase(phases);
  }, [phases]);

  useEffect(() => {
    if (!isEmpty(phases)) {
      setPhase(phases);
      setIsEdit(false);
    }
  }, [phases]);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setPhase(null);
    } else {
      setModal(true);
    }
  }, [modal]);

  const handlePhaseClick = useCallback(
    (arg) => {
      const phase = arg;
      const associatedProject = projects.find(
        (proj) => proj.id === phase.project
      );
      if (!associatedProject) {
        toast.error("Project data is not loaded yet. Please try again later.");
        return;
      }

      setPhase({
        id: phase.id,
        project: phase.project,
        name: phase.name,
        description: phase.description,
        starting_date: phase.starting_date,
        estimated_finish_date: phase.estimated_finish_date,
        actual_finish_date: phase.actual_finish_date,
        equity_capital: phase.equity_capital,
        createdAt: phase.createdAt,
        active: phase.active,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle, projects]
  );

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");
    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);

  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const checkall = document.getElementById("checkBoxAll");
    selectedCheckBoxDelete.forEach((element) => {
      dispatch(onDeletePhase(element.value));
      setTimeout(() => {
        toast.clearWaitingQueue();
      }, 3000);
    });
    setIsMultiDeleteButton(false);
    checkall.checked = false;
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    ele.length > 0
      ? setIsMultiDeleteButton(true)
      : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  const columns = useMemo(
    () => [
      {
        header: "Project",
        accessorKey: "project",
        enableColumnFilter: false,
        cell: (cell) => {
          const projectId = cell.getValue();
          const project = projects.find((proj) => proj.id === projectId);
          return project ? project.name : <></>;
        },
      },

      {
        header: "name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
      },
      {
        header: "Starting Date",
        accessorKey: "starting_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return (
            <>
              {handleValidDate(value)}
            </>
          );
        },
      },
      {
        header: "Estimated Finish Date",
        accessorKey: "estimated_finish_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return <>{handleValidDate(value)}</>;
        },
      },

      {
        header: "Actual Finish Date",
        accessorKey: "actual_finish_date",
        enableColumnFilter: false,
        cell: (cell) => {
          const value = cell.getValue();
          if (value === null || value === undefined) {
            return <span style={{ color: "red" }}>No data</span>;
          }
          return <>{handleValidDate(value)}</>;
        },
      },
      {
        header: "Active",
        accessorKey: "active",
        enableColumnFilter: false,
        cell: (cell) => <>{cell.getValue() ? "True" : "False"}</>,
      },

      {
        header: "Action",
        cell: (cellProps) => {
          const isAdmin = userProfile?.user?.groups?.includes("Admin");

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item">
                <Link
                  to={`/phase-details/${cellProps.row.original.id}`}
                  className="text-primary d-inline-block"
                >
                  <i className="ri-eye-fill fs-16"></i>
                </Link>
              </li>
              {isAdmin && (
                <>
                  <li className="list-inline-item edit">
                    <Link
                      to="#"
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={() => {
                        const phaseData = cellProps.row.original;
                        handlePhaseClick(phaseData);
                      }}
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </Link>
                  </li>
                  <li className="list-inline-item">
                    <Link
                      to="#"
                      className="text-danger d-inline-block remove-item-btn"
                      onClick={() => {
                        const phaseData = cellProps.row.original;
                        onClickDelete(phaseData);
                      }}
                    >
                      <i className="ri-delete-bin-5-fill fs-16"></i>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          );
        },
      },
    ],
    [handlePhaseClick, checkedAll]
  );

  const defaultdate = () => {
    let d = new Date(),
      months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    let h = d.getHours() % 12 || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return (
      d.getDate() +
      " " +
      months[d.getMonth()] +
      ", " +
      d.getFullYear() +
      ", " +
      h +
      ":" +
      d.getMinutes() +
      " " +
      ampm
    ).toString();
  };

  const handleValidDate = (date) => {
    const date1 = moment(new Date(date)).format("DD MMM Y");
    return date1;
  };

  const [isExportCSV, setIsExportCSV] = useState(false);

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={phaseList}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletePhase}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        {/* <BreadCrumb title="Phases" pageTitle="Tables" /> */}
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm"></div>
                  <div className="col-sm-auto" style={{ marginBottom: "10px" }}>
                    <div className="d-flex gap-1 flex-wrap">
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={() => {
                            setIsEdit(false);
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Create Phase
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                        Export
                      </button>{" "}
                      {isMultiDeleteButton && (
                        <button
                          className="btn btn-soft-danger"
                          onClick={() => setDeleteModalMulti(true)}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  {isPhaseSuccess ? (
                    projects.length === 0 ? (
                      <div className="text-center mt-5">
                        <p className="text-danger">
                          No projects are available. Please add a project before
                          managing phases.
                        </p>
                      </div>
                    ) : phases.length > 0 ? (
                      <TableContainer
                        columns={columns}
                        data={phaseList || []}
                        // isGlobalFilter={true}
                        isAddUserList={false}
                        customPageSize={20}
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted text-uppercase"
                        handleOrderClick={handlePhaseClick}
                        // isOrderFilter={true}
                        SearchPlaceholder="Search for ID, name, active or something..."
                      />
                    ) : (
                      <div className="text-center mt-5">
                        <p className="text-muted">
                          No phases are introduced yet. Please add a new phase.
                        </p>
                      </div>
                    )
                  ) : (
                    <Loader error={error} />
                  )}
                </div>
                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? "Edit Phase" : "Add Phase"}
                  </ModalHeader>

                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <input type="hidden" id="id-field" />

                      <div className="mb-3">
                        <Label htmlFor="project-field" className="form-label">
                          Project
                        </Label>
                        <Input
                          type="select"
                          name="project"
                          id="project-field"
                          className="form-select"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.project || ""}
                          invalid={
                            validation.touched.project &&
                            validation.errors.project
                              ? true
                              : false
                          }
                        >
                          <option value="">Select a Project</option>
                          {projects.map((proj) => (
                            <option key={proj.id} value={proj.id}>
                              {proj.name}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.project &&
                          validation.errors.project && (
                            <FormFeedback type="invalid">
                              {validation.errors.project}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="name-field" className="form-label">
                          Name
                        </Label>
                        <Input
                          name="name"
                          id="name-field"
                          className="form-control"
                          placeholder="Enter Name"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name && validation.errors.name && (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        )}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="description-field"
                          className="form-label"
                        >
                          Description
                        </Label>
                        <Input
                          name="description"
                          id="description-field"
                          className="form-control"
                          placeholder="Enter Description"
                          type="textarea"
                          rows="3"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.description || ""}
                          invalid={
                            validation.touched.description &&
                            validation.errors.description
                              ? true
                              : false
                          }
                        />
                        {validation.touched.description &&
                          validation.errors.description && (
                            <FormFeedback type="invalid">
                              {validation.errors.description}
                            </FormFeedback>
                          )}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="starting_date-field"
                          className="form-label"
                        >
                          Starting Date
                        </Label>
                        <Flatpickr
                          name="starting_date"
                          id="starting_date-field"
                          className={`form-control ${
                            validation.touched.starting_date &&
                            validation.errors.starting_date
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select Starting Date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue(
                              "starting_date",
                              moment(date[0]).toISOString()
                            )
                          }
                          value={validation.values.starting_date || ""}
                          onBlur={() =>
                            validation.setFieldTouched("starting_date", true)
                          }
                          invalid={
                            validation.touched.starting_date &&
                            validation.errors.starting_date
                              ? true
                              : false
                          }
                        />
                        {validation.touched.starting_date &&
                          validation.errors.starting_date && (
                            <FormFeedback type="invalid">
                              {validation.errors.starting_date}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="estimated_finish_date-field"
                          className="form-label"
                        >
                          Estimated Finish Date
                        </Label>
                        <Flatpickr
                          name="estimated_finish_date"
                          id="estimated_finish_date-field"
                          className={`form-control ${
                            validation.touched.estimated_finish_date &&
                            validation.errors.estimated_finish_date
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select Estimated Finish Date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue(
                              "estimated_finish_date",
                              moment(date[0]).toISOString()
                            )
                          }
                          value={validation.values.estimated_finish_date || ""}
                          onBlur={() =>
                            validation.setFieldTouched(
                              "estimated_finish_date",
                              true
                            )
                          }
                          invalid={
                            validation.touched.estimated_finish_date &&
                            validation.errors.estimated_finish_date
                              ? true
                              : false
                          }
                        />
                        {validation.touched.estimated_finish_date &&
                          validation.errors.estimated_finish_date && (
                            <FormFeedback type="invalid">
                              {validation.errors.estimated_finish_date}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="actual_finish_date-field"
                          className="form-label"
                        >
                          Actual Finish Date
                        </Label>
                        <Flatpickr
                          name="actual_finish_date"
                          id="actual_finish_date-field"
                          className={`form-control ${
                            validation.touched.actual_finish_date &&
                            validation.errors.actual_finish_date
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="Select Actual Finish Date"
                          options={{
                            enableTime: true,
                            altInput: true,
                            altFormat: "d M, Y, G:i K",
                            dateFormat: "d M, Y, G:i K",
                          }}
                          onChange={(date) =>
                            validation.setFieldValue(
                              "actual_finish_date",
                              moment(date[0]).toISOString()
                            )
                          }
                          value={validation.values.actual_finish_date || ""}
                          onBlur={() =>
                            validation.setFieldTouched(
                              "actual_finish_date",
                              true
                            )
                          }
                          invalid={
                            validation.touched.actual_finish_date &&
                            validation.errors.actual_finish_date
                              ? true
                              : false
                          }
                        />
                        {validation.touched.actual_finish_date &&
                          validation.errors.actual_finish_date && (
                            <FormFeedback type="invalid">
                              {validation.errors.actual_finish_date}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="equity_capital-field"
                          className="form-label"
                        >
                          Equity Capital (EUR)
                        </Label>
                        <Input
                          name="equity_capital"
                          id="equity_capital-field"
                          className="form-control"
                          placeholder="Owner's invested capital for this phase (EUR)"
                          type="number"
                          step="0.01"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.equity_capital ?? ""}
                        />
                        <small className="text-muted">
                          Used as the ROE denominator in reporting. Leave blank if unknown.
                        </small>
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="delivered-status"
                          className="form-label"
                        >
                          Active
                        </Label>

                        <Input
                          name="active"
                          type="select"
                          className="form-select"
                          onChange={(e) =>
                            validation.setFieldValue(
                              "active",
                              e.target.value === "true"
                            )
                          }
                          onBlur={validation.handleBlur}
                          value={validation.values.active ? "true" : "false"}
                        >
                          <option value="true">True</option>
                          <option value="false">False</option>
                        </Input>

                        {validation.touched.active &&
                        validation.errors.active ? (
                          <FormFeedback type="invalid">
                            {validation.errors.active}
                          </FormFeedback>
                        ) : null}
                      </div>
                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setModal(false);
                          }}
                        >
                          Close
                        </button>

                        <button type="submit" className="btn btn-success">
                          {!!isEdit ? "Update" : "Add Phase"}
                        </button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PhaseMenegement;
